import { updateMasterProductApi, useMasterProduct } from "@/api/services/admin/master-data/product";
import { TUpdateMasterProductReqDto } from "@/api/services/admin/master-data/product/dtos/update-master-product.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { ProductFormFields } from "@/features/master-data/components/Product/ProductFormFields";
import { MASTER_DATA_TYPE } from "@/features/master-data/constants/masterDataType";
import { masterDataProductSchema } from "@/features/master-data/schema/masterDataProductSchema";
import { TMasterDataProductForm } from "@/features/master-data/types/masterDataProductForm";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, CardContent, CardHeader, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

const listRoute = getDynamicRoute(AppRoutes.masterDataDetail, { type: MASTER_DATA_TYPE.PRODUCT });

const MasterDataProductEditPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: response, isLoading } = useMasterProduct();
  const product = response?.data;

  const methods = useForm<TMasterDataProductForm>({
    resolver: yupResolver(masterDataProductSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, reset } = methods;

  useEffect(() => {
    if (!product) return;
    reset({
      name: product.name ?? "",
      logo: product.logo ?? "",
      introductionPageUrl: product.introductionPageUrl ?? "",
      apiEndpoint: product.apiEndpoint ?? "",
      singleCharLabel: product.singleCharLabel ?? "",
      publishedAt: product.publishedAt ? dayjs(product.publishedAt) : null,
    });
  }, [product, reset]);

  const onSubmit = async (data: TMasterDataProductForm) => {
    if (!productId) return;
    try {
      const payload: TUpdateMasterProductReqDto = {
        name: data.name,
        logo: data.logo,
        introductionPageUrl: data.introductionPageUrl || null,
        apiEndpoint: data.apiEndpoint,
        singleCharLabel: data.singleCharLabel,
        publishedAt: data.publishedAt ? data.publishedAt.toISOString() : null,
      };
      await updateMasterProductApi(productId, payload);
      toast.success(API_SUCCESS_MESSAGE);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MASTER_DATA.FETCH_PRODUCT_LIST] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MASTER_DATA.FETCH_PRODUCT_DETAIL, productId] }),
      ]);
      navigate(listRoute);
    } catch (error) {
      setErrorForm(methods, error);
      showError(error);
    }
  };

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "基本情報",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  基本情報
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <ProductFormFields control={control} createdAt={product?.createdAt} updatedAt={product?.updatedAt} />
            </CardContent>
          </Card>
        ),
      },
    ],
    [control, product?.createdAt, product?.updatedAt],
  );

  if (isLoading) return <LoadingPage />;

  return (
    <Form height="100%" methods={methods} onSubmit={onSubmit} schema={masterDataProductSchema}>
      <Stack gap={3}>
        <Stack gap={1}>
          <Typography variant="head20Bold" color={text.primary}>
            プロダクトの編集
          </Typography>
        </Stack>

        <MenuNavigate
          contents={contents}
          contentAffix={
            <StickyFooter justifyContent="space-between">
              <Button size="sm" variant="ghost" onClick={() => navigate(listRoute)}>
                戻る
              </Button>
              <Button size="sm" type="submit">
                保存する
              </Button>
            </StickyFooter>
          }
        />
      </Stack>
    </Form>
  );
};

export default MasterDataProductEditPage;
